import React, { useState, useEffect } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { BrowserRouter as Router, Navigate, useLocation, useNavigate } from "react-router-dom";
import { loginRequest, apiRequest } from "./authConfig";
import { getCollaborateurByEmail, setTokenProvider } from "./services/api";
import CoqueV2 from "./pages/CoqueV2";
import "./App.css";

// ===================================================================
// The Crowd — point d'entree
//
// Bascule franche vers la v2 : l'ancienne interface est retiree. Toute
// l'application authentifiee est servie par la coque v2 (CoqueV2, sous
// /v2). Les anciennes adresses redirigent vers leur equivalent v2, pour
// que les favoris et les liens deja partages continuent de fonctionner.
// ===================================================================

const ROLES_PILOTAGE = ["manager", "directeur"];

// Ancienne adresse -> adresse v2. /projets depend du profil : la vue
// manager ouvrait la gestion des projets, la vue collaborateur Mes projets.
function adresseV2(pathname, collaborateur) {
  const fiche = pathname.match(/^\/equipe\/(\d+)\/?$/);
  if (fiche) return `/v2/pilotage/equipe/${fiche[1]}`;

  const correspondances = {
    "/saisie": "/v2/activite/saisie",
    "/jira": "/v2/activite/tickets",
    "/projets-dashboard": "/v2/pilotage/projets",
    "/dashboard": "/v2/pilotage/dashboard",
    "/equipe": "/v2/pilotage/equipe",
    "/taches": "/v2/pilotage/taches",
    "/conversations": "/v2/conversations",
    "/change-management": "/v2/change-management",
    "/bibliotheques": "/v2/bibliotheques",
    "/creation-compte": "/v2/utilisateurs/accueil",
    "/comptes-ad": "/v2/utilisateurs/comptes-ad",
  };
  const chemin = pathname.replace(/\/+$/, "") || "/";
  if (chemin === "/projets") {
    return ROLES_PILOTAGE.includes(collaborateur?.role) ? "/v2/pilotage/projets" : "/v2/activite/projets";
  }
  return correspondances[chemin] || "/v2";
}

function AppContent() {
  const { instance, accounts, inProgress } = useMsal();
  const [collaborateur, setCollaborateur] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  // Enregistre le fournisseur de token pour l'API TheCrowd — doit s'exécuter
  // avant tout appel apiFetch (voir services/api.js). Tente silencieux
  // d'abord, popup en secours si le scope n'a pas encore été consenti.
  useEffect(() => {
    setTokenProvider(async () => {
      const account = instance.getActiveAccount() || accounts[0];
      if (!account) return null;
      try {
        const result = await instance.acquireTokenSilent({ ...apiRequest, account });
        return result.accessToken;
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          const result = await instance.acquireTokenPopup({ ...apiRequest, account });
          return result.accessToken;
        }
        throw err;
      }
    });
  }, [instance, accounts]);

  useEffect(() => {
    if (inProgress === "none" && accounts.length > 0) {
      const account = accounts[0];
      instance.setActiveAccount(account);

      getCollaborateurByEmail(account.username)
        .then(setCollaborateur)
        .catch((err) => {
          console.warn("Collaborateur non trouvé en base:", err);
        })
        .finally(() => setLoading(false));
    } else if (inProgress === "none" && accounts.length === 0) {
      setLoading(false);
    }
  }, [accounts, inProgress, instance]);

  const handleLogin = () => {
    instance.loginRedirect(loginRequest);
  };

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  // Tous les modules du catalogue ont leur page v2 (routeV2) : la coque les
  // ouvre elle-meme. Ce rappel ne sert que de filet pour une entree sans page.
  const ouvrirModule = (module) => navigate(module.routeV2 || "/v2");

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner" />
        <span>Chargement...</span>
      </div>
    );
  }

  const estV2 = location.pathname === "/v2" || location.pathname.startsWith("/v2/");

  return (
    <>
      <AuthenticatedTemplate>
        {estV2 ? (
          <CoqueV2
            collaborateur={collaborateur}
            nomUtilisateur={accounts[0]?.name}
            onLogout={handleLogout}
            onOuvrirModule={ouvrirModule}
          />
        ) : (
          <Navigate to={adresseV2(location.pathname, collaborateur)} replace />
        )}
      </AuthenticatedTemplate>

      <UnauthenticatedTemplate>
        <div className="login-container">
          <img
            src="/Hopscotch-Square-Black_White-Typo.png"
            alt="Hopscotch"
            style={{ height: "48px", marginBottom: "8px" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
          <h2>The Crowd</h2>
          <p>Connectez-vous avec votre compte Hopscotch</p>
          <button onClick={handleLogin} className="btn-login">
            Se connecter
          </button>
        </div>
      </UnauthenticatedTemplate>
    </>
  );
}

// Le Router englobe toute l'application pour que AppContent puisse lire
// l'URL (useLocation) et rediriger les anciennes adresses.
function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
